<template>
  <div>
    <div v-if="!polling && sales.length == 0">
      This user has not had any sales yet.
    </div>
    <div v-if="!polling && sales.length > 0">
      <label for="searchText" class="block text-sm font-medium text-gray-700"
        >Search Text</label
      >
      <div class="mt-1">
        <input
          type="text"
          name="searchText"
          id="searchText"
          class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
          placeholder="Query Text"
          v-model="searchText"
        />
      </div>
    </div>

    <div
      class="mt-4 bg-gray-50 border border-gray-200 flex items-center content-center justify-center py-24"
      v-if="polling"
    >
      <DotPulse />
    </div>

    <ul
      class="mt-4 space-y-2 divide-gray-200"
      v-if="!polling && sales.length > 0"
    >
      <li v-for="sale in sales" v-bind:key="sale.id">
        <div
          class="block rounded-md overflow-hidden bg-white border border-gray-200 cursor-pointer"
          v-on:click="clickedSale(sale)"
        >
          <div class="px-4 py-4 sm:px-6">
            <div class="flex items-center justify-between relative">
              <p class="text-sm font-medium text-gray-800 truncate">
                {{ sale.video.title }}
              </p>
              <div class="absolute top-0 right-0 flex flex-row space-x-2">
                <span
                  class="text-xs font-medium px-2 py-1 rounded-full uppercase bg-gray-200 text-gray-700"
                  v-if="sale.invoice_number"
                  >{{ sale.invoice_number }}</span
                >
                <span
                  class="text-xs font-medium px-2 py-1 rounded-full uppercase"
                  :class="{
                    'bg-yellow-100 text-yellow-700':
                      sale.deleted_at == null && sale.status == 'pending',
                    'bg-green-100 text-green-700':
                      sale.deleted_at == null && sale.status == 'complete',
                    'bg-blue-100 text-blue-700':
                      sale.deleted_at == null && sale.status == 'occurring',
                    'bg-red-100 text-red-700': sale.deleted_at !== null
                  }"
                  >{{ sale.deleted_at == null ? sale.status : "Deleted" }}</span
                >
              </div>
            </div>
            <div class="mt-2 sm:flex sm:justify-between">
              <div class="sm:flex">
                <div
                  class="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 space-x-1"
                >
                  <!-- Heroicon name: solid/users -->
                  <span class="flex-shrink-0 h-5 w-5 flex items-center">
                    <i class="fad fa-building"></i>
                  </span>
                  <p>{{ sale.network.name }}</p>
                </div>
                <div
                  class="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6 space-x-1"
                >
                  <!-- Heroicon name: solid/users -->
                  <span class="flex-shrink-0 h-5 w-5 flex items-center">
                    <i class="fas fa-user"></i>
                  </span>
                  <p>{{ sale.purchaser.name }}</p>
                </div>
                <div
                  class="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6 space-x-1"
                >
                  <!-- Heroicon name: solid/users -->
                  <span class="flex-shrink-0 h-5 w-5 flex items-center">
                    <i class="fas fa-coins"></i>
                  </span>
                  <p>
                    ${{ sale.price }}
                    <span v-if="sale.stringer_cut">
                      / ${{ sale.stringer_cut }}</span
                    >
                  </p>
                </div>
                <!-- <div
                  class="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6"
                  v-tippy
                  title="Invoice Reference"
                >
                  <span class="flex-shrink-0 h-5 w-5 flex items-center">
                    <i class="fad fa-file-invoice"></i>
                  </span>
                  <p>{{ sale.invoice_number }}</p>
                </div> -->
              </div>

              <div
                class="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 space-x-1"
              >
                <!-- Heroicon name: solid/calendar -->
                <span class="flex-shrink-0 h-5 w-5 flex items-center">
                  <i class="fad fa-calendar-check"></i>
                </span>
                <p>
                  Purchased on
                  <span
                    >{{ sale.created_at | displayLocalDateTime("M/D/YYYY") }}
                    at
                    {{ sale.created_at | displayLocalDateTime("h:mm a") }}</span
                  >
                </p>
              </div>
            </div>
          </div>
        </div>
      </li>
    </ul>

    <div
      class="flex flex-row justify-between items-center p-4 bg-white rounded mt-2"
      v-if="!polling && sales.length > 0"
    >
      <span>
        Showing
        <strong class="font-medium">{{ paginationInfo.from }}</strong> to
        <strong class="font-medium">{{ paginationInfo.to }}</strong> of
        <strong class="font-medium">{{ paginationInfo.total }}</strong>
        results
      </span>
      <div class="flex flex-row items-center space-x-4">
        <select
          id="country"
          name="country"
          autocomplete="country-name"
          class="focus:ring-gray-500 focus:border-gray-500 shadow-sm text-sm border-gray-300 rounded-md -m-2"
          v-model="perPage"
        >
          <option v-for="amount in paginationSelector" v-bind:key="amount">{{
            amount
          }}</option>
        </select>
        <span>per page</span>
      </div>
      <div class="flex items-center justify-end">
        <div class="py-3 border rounded-lg dark:border-gray-600">
          <ol
            class="flex items-center text-sm text-gray-500 divide-x divide-gray-300 dark:text-gray-400"
          >
            <li
              v-for="(page, key) in pageRange"
              :key="key"
              v-on:click="goToPage(page)"
            >
              <button
                type="button"
                class="relative flex items-center justify-center font-medium min-w-[2rem] px-2 h-8 -my-3 rounded-md focus:outline-none filament-tables-pagination-item transition text-primary-600 focus:underline "
                :class="{
                  'bg-gray-500 bg-opacity-20 ring-1 ring-gray-600':
                    page == paginationInfo.current_page,
                  'hover:bg-gray-400 hover:bg-opacity-20 cursor-pointer':
                    page !== '...',
                  'cursor-default': page == '...'
                }"
                :disabled="page == '...'"
              >
                <span>{{ page }}</span>
              </button>
            </li>
          </ol>
        </div>
      </div>
    </div>
    <ApproveVideoSaleModal />
    <RestoreSaleModal />
  </div>
</template>

<script>
import * as Config from "@/config";
import { debounce } from "@/helpers";

import DotPulse from "@/components/Loaders/DotPulse";
import ApproveVideoSaleModal from "@/screens/admin/modals/ApproveVideoSaleModal";
import RestoreSaleModal from "@/screens/admin/modals/RestoreSaleModal";

export default {
  name: "AdminUserSales",
  props: {
    userId: {
      type: Number
    },
    userData: {
      type: Object
    }
  },
  components: { DotPulse, ApproveVideoSaleModal, RestoreSaleModal },
  data() {
    return {
      sales: [],
      searchText: null,
      polling: true,

      perPage: 25,
      limit: 2,
      currentPage: 0,
      paginationInfo: {
        current_page: null,
        from: null,
        last_page: null,
        per_page: null,
        to: null,
        total: null
      },
      paginationSelector: [10, 25, 50]
    };
  },
  mounted() {
    this.getSales();
  },

  methods: {
    async getSales() {
      this.polling = true;

      let urlPath =
        "/api/v2/admin/users/" +
        this.userData.id +
        "/sales?paginate=true&tableRecordsPerPage=" +
        this.perPage +
        "&page=" +
        this.currentPage;

      if (this.searchText !== null && this.searchText !== "") {
        urlPath += "&searchQuery=" + this.searchText;
      }

      let res = await this.$http.get(Config.apiPath + urlPath);
      this.$set(this, "sales", res.data.data);
      this.$set(this, "polling", false);
      this.paginationInfo = {
        current_page: res.data.current_page,
        from: res.data.from,
        last_page: res.data.last_page,
        per_page: res.data.per_page,
        to: res.data.to,
        total: res.data.total
      };
    },
    goToPage(page) {
      if (page == "...") {
        return;
      }
      this.currentPage = page;
      this.getSales();
    },
    async clickedSale(sale) {
      if (sale.deleted_at !== null) {
        this.$eventBus.$emit("admin-sale-deleted-open-modal", sale);
        return;
      }
      if (sale.status == "pending") {
        this.$eventBus.$emit("admin-approve-sale-open-modal", sale);
        return;
      }
      if (sale.status == "occurring") {
        let result = await this.$swal({
          title: "Continue?",
          text: "Are you sure you want to finalize this sale?",
          type: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, finalize."
        });

        if (result.isConfirmed) {
          await this.$http.post(
            Config.apiPath + "/api/v2/admin/sales/finalize",
            {
              id: sale.id
            }
          );
          this.getSales();
        }
      }
      if (sale.status == "complete") {
        alert("Sale information will display in the future");
        return;
      }
    }
  },
  computed: {
    pageRange() {
      if (this.limit === -1) {
        return 0;
      }
      if (this.limit === 0) {
        return this.paginationInfo.last_page;
      }
      var current = this.paginationInfo.current_page;
      var last = this.paginationInfo.last_page;
      var delta = this.limit;
      var left = current - delta;
      var right = current + delta + 1;
      var range = [];
      var pages = [];
      var l;
      for (var i = 1; i <= last; i++) {
        if (i === 1 || i === last || (i >= left && i < right)) {
          range.push(i);
        }
      }
      range.forEach(function(i) {
        if (l) {
          if (i - l === 2) {
            pages.push(l + 1);
          } else if (i - l !== 1) {
            pages.push("...");
          }
        }
        pages.push(i);
        l = i;
      });
      return pages;
    }
  },
  watch: {
    searchText() {
      this.currentPage = 0;
      this.getSales();
    },
    perPage() {
      this.currentPage = 0;
      this.getSales();
    }
  }
};
</script>
